import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { selectMessageLogDetailsByMessageId, selectMessageLogGroupsByMessageId } from "@common-reducers/CampaignSelectors";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import { MessageLogCard } from "./MessageLogCard";
import { MessageLogStatusBadge } from "./MessageLogStatusBadge";
import { MessageLogMetadata } from "./MessageLogMetadata";
import { MessageStatusWithError } from "./MessageStatusWithError";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronRight, Repeat, AlertTriangle, AlertCircle, CheckCircle, Server } from "lucide-react";
import { useState, Fragment, useCallback, useMemo, useEffect, useRef } from "react";
import moment from "moment";
import { MessageLog } from "@common/models/message-log";

interface IssuesSummary {
    hasProblems: boolean;
    issues: string[];
    severity: 'error' | 'warning' | 'none';
    multipleGateways: boolean;
    duplicateSends: number;
}

/**
 * Analyze logs for issues - returns summary for display in table
 */
function analyzeLogsForIssues(logGroups: MessageLog[][]): IssuesSummary {
    const allLogs = logGroups.flat();
    const issues: string[] = [];
    let severity: 'error' | 'warning' | 'none' = 'none';

    // Check for multiple WhatsApp versions (multiple gateways)
    const waVersions = new Set<string>();
    allLogs.forEach(log => {
        if (log.metadata?.waVersion) {
            waVersions.add(log.metadata.waVersion);
        }
    });
    const multipleGateways = waVersions.size > 1;
    if (multipleGateways) {
        issues.push(`Multiple gateways: ${Array.from(waVersions).map(v => `v${v}`).join(', ')}`);
        severity = 'warning';
    }

    // Count "sent successfully" logs - more than 1 means duplicate
    let sentCount = 0;
    allLogs.forEach(log => {
        const msg = log.metadata?.logMessage || '';
        if (msg.includes('sent successfully')) {
            sentCount++;
        }
    });
    if (sentCount > 1) {
        issues.push(`DUPLICATE: Message sent ${sentCount} times!`);
        severity = 'error';
    }

    // Check for failed status (use String() since runtime data may include 'failed')
    const hasFailed = allLogs.some(log =>
        String(log.status) === 'failed' ||
        (log.metadata?.logMessage || '').toLowerCase().includes('failed')
    );
    if (hasFailed) {
        issues.push('Send failed');
        severity = 'error';
    }

    return {
        hasProblems: issues.length > 0,
        issues,
        severity,
        multipleGateways,
        duplicateSends: sentCount
    };
}

interface MessageLogTableProps {
    messageIds: string[];
    onMediaClick?: (message: any) => void;
}

const INITIAL_ROWS_TO_RENDER = 50;
const ROWS_PER_LOAD = 50;

export function MessageLogTable({ messageIds, onMediaClick }: MessageLogTableProps) {
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
    const [visibleRowsCount, setVisibleRowsCount] = useState(INITIAL_ROWS_TO_RENDER);
    const loadMoreRef = useRef<HTMLDivElement>(null);

    const toggleRow = useCallback((id: string) => {
        setExpandedRows(prev => {
            const isExpanded = prev[id];
            return {
                ...prev,
                [id]: !isExpanded
            };
        });
    }, []);

    // Reset visible rows when messageIds change
    useEffect(() => {
        setVisibleRowsCount(INITIAL_ROWS_TO_RENDER);
    }, [messageIds.length]);

    // Infinite scroll with Intersection Observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const firstEntry = entries[0];
                if (firstEntry?.isIntersecting) {
                    setVisibleRowsCount(prev => {
                        const newCount = Math.min(prev + ROWS_PER_LOAD, messageIds.length);
                        return newCount;
                    });
                }
            },
            {
                root: null,
                rootMargin: '100px', // Start loading 100px before reaching the bottom
                threshold: 0.1
            }
        );

        const currentRef = loadMoreRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [messageIds.length, visibleRowsCount]);

    const visibleMessageIds = useMemo(() => {
        return messageIds.slice(0, visibleRowsCount);
    }, [messageIds, visibleRowsCount]);

    const hasMore = visibleRowsCount < messageIds.length;

    if (messageIds.length === 0) {
        return (
            <div className="text-center py-8 text-slate-500">
                No message logs found
            </div>
        );
    }

    return (
        <div className="rounded-md border">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[50px]"></TableHead>
                        <TableHead className="w-[50px]">Issues</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Logs</TableHead>
                        <TableHead>Latest Metadata</TableHead>
                        <TableHead>Latest Update</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {visibleMessageIds.map((messageId, index) => (
                        <MessageLogTableRow 
                            key={messageId}
                            messageId={messageId}
                            isExpanded={expandedRows[messageId] || false}
                            onToggle={() => toggleRow(messageId)}
                            onMediaClick={onMediaClick}
                            index={index}
                        />
                    ))}
                </TableBody>
            </Table>
            {hasMore && (
                <div 
                    ref={loadMoreRef}
                    className="flex justify-center py-4 border-t"
                >
                    <div className="text-sm text-slate-500">
                        Loading more... ({messageIds.length - visibleRowsCount} remaining)
                    </div>
                </div>
            )}
        </div>
    );
}

function MessageLogTableRow({ 
    messageId, 
    isExpanded, 
    onToggle, 
    onMediaClick,
    index
}: { 
    messageId: string; 
    isExpanded: boolean; 
    onToggle: () => void;
    onMediaClick?: (message: any) => void;
    index: number;
}) {
    const messageDetails = useAppSelector(state => 
        selectMessageLogDetailsByMessageId(state, messageId)
    );
    
    const logGroups = useAppSelector(state => 
        selectMessageLogGroupsByMessageId(state, messageId)
    );

    if (!messageDetails) return null;

    const allLogs = logGroups.flat();
    const latestLog = allLogs.length > 0
        ? allLogs.reduce((latest, current) => {
            return new Date(current.createdAt ?? 0) > new Date(latest.createdAt ?? 0) ? current : latest;
        }, allLogs[0])
        : undefined;

    const isEvenRow = index % 2 === 0;
    const rowBgClass = isEvenRow ? 'bg-slate-50' : 'bg-white';

    // Analyze logs for issues
    const issuesSummary = useMemo(() => analyzeLogsForIssues(logGroups), [logGroups]);

    return (
        <Fragment>
            <TableRow
                className={`cursor-pointer ${rowBgClass} ${issuesSummary.severity === 'error' ? 'bg-red-50 dark:bg-red-950/30' : issuesSummary.severity === 'warning' ? 'bg-yellow-50 dark:bg-yellow-950/30' : ''}`}
                onClick={onToggle}
            >
                <TableCell>
                    <Collapsible open={isExpanded} onOpenChange={onToggle}>
                        <CollapsibleTrigger asChild>
                            <div
                                className="cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <ChevronRight
                                    className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                />
                            </div>
                        </CollapsibleTrigger>
                    </Collapsible>
                </TableCell>
                <TableCell>
                    {issuesSummary.hasProblems ? (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div className="cursor-help flex items-center justify-center">
                                    {issuesSummary.severity === 'error' ? (
                                        <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                                    ) : (
                                        <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                                    )}
                                </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                                <div className="space-y-1">
                                    <p className="font-semibold text-sm">
                                        {issuesSummary.severity === 'error' ? '⚠️ Error Detected' : '⚡ Warning'}
                                    </p>
                                    <ul className="text-xs space-y-0.5">
                                        {issuesSummary.issues.map((issue, i) => (
                                            <li key={i} className="flex items-start gap-1">
                                                <span>•</span>
                                                <span>{issue}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    ) : (
                        <div className="flex items-center justify-center">
                            <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400 opacity-50" />
                        </div>
                    )}
                </TableCell>
                <TableCell>
                    <div className="max-w-[300px]">
                        <div className="flex items-center gap-2">
                            <div className="text-sm font-medium truncate flex-1">
                                {messageDetails.message?.message?.substring(0, 60) || 'No content'}{messageDetails.message?.message && messageDetails.message.message.length > 60 ? '...' : ''}
                            </div>
                            {messageDetails.message?.isRecurring && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className="cursor-help">
                                            <Repeat className="h-4 w-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                        <p className="text-xs">Recurring message</p>
                                    </TooltipContent>
                                </Tooltip>
                            )}
                        </div>
                        {messageDetails.message?.contactList?.[0]?.id && (
                            <div className="text-xs text-slate-500 truncate">
                                {messageDetails.message.contactList[0].name || messageDetails.message.contactList[0].id}
                            </div>
                        )}
                    </div>
                </TableCell>
                <TableCell>
                    <MessageStatusWithError messageId={messageId} />
                </TableCell>
                <TableCell>
                    <span className="text-sm">{messageDetails.logCount || 0}</span>
                </TableCell>
                <TableCell>
                    {latestLog?.metadata && (
                        <MessageLogMetadata metadata={latestLog.metadata} />
                    )}
                </TableCell>
                <TableCell>
                    <span className="text-sm text-slate-600">
                        {moment(messageDetails.latestUpdate).format('MM/DD/YYYY, h:mm:ss A')}
                    </span>
                </TableCell>
            </TableRow>
            {isExpanded && (
                <TableRow className={rowBgClass}>
                    <TableCell colSpan={7} className="p-0">
                        <div className={`p-4 ${isEvenRow ? 'bg-slate-50' : 'bg-white'}`}>
                            <MessageLogCard messageId={messageId} onMediaClick={onMediaClick} />
                        </div>
                    </TableCell>
                </TableRow>
            )}
        </Fragment>
    );
}

